import * as React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Prompt} from 'react-router-dom';

import {SavedFilters} from 'nutshell-core/saved-filters';

import {AiSuggestionPill} from 'shells/ai-suggestion-pill/ai-suggestion-pill';
import {Stack} from 'shells/layout/stack/stack';
import {PromptBox} from 'shells/prompt-box/prompt-box';
import {useNutshellTour} from 'shells/tours/use-nutshell-tour';
import {Body} from 'shells/typography/body';

import {AiFeatureType} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';
import type {NutshellState} from '../../../store';

import {useIsAiFeatureEnabled} from '../../setup/ai-features/graphql/hooks/use-is-ai-feature-enabled';

import {readPrompt, removePrompt, writeBannerInfo, writePrompt} from '../banner/helpers';
import {toggleShowFilterDrawer} from '../reports-actions';

import {useGenerateReport} from './graphql/hooks/use-generate-report';

import styles from './prompt-section.module.css';

const STARTER_PROMPTS = [
    {
        label: 'How much business has each member of my team closed this month?',
        prompt: 'How much business has each member of my team closed this month?',
    },
    {
        label: 'Where are my top leads coming from?',
        prompt: 'Where are my top leads coming from?',
    },
    {
        label: 'What is my best sales week this quarter?',
        prompt: 'What is my best sales week this quarter?',
    },
];

type Props = {
    onGenerate: () => void;
};

export function PromptSection(props: Props) {
    const [prompt, setPrompt] = React.useState(readPrompt());
    const textareaRef = React.useRef<HTMLTextAreaElement | null | undefined>(null);
    const autoSubmittedRef = React.useRef(false);

    const {isEnabled: isReportGeneratorEnabled, isLoading: isLoadingReportGeneratorEnabled} =
        useIsAiFeatureEnabled(AiFeatureType.REPORT_GENERATOR);

    const {generateReport} = useGenerateReport();
    const {pauseTour} = useNutshellTour();

    const isFilterDrawerOpen = useSelector(
        (state: NutshellState) => state.reports.isFilterDrawerOpen
    );
    const dispatch = useDispatch();

    const focusPromptBox = () => {
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    const handleSubmit = () => {
        props.onGenerate();
        pauseTour();
        generateReport({prompt}).then(({data}) => {
            if (!data) {
                return;
            }

            if (!isFilterDrawerOpen) {
                dispatch(toggleShowFilterDrawer());
            }

            const {savedFilter, status} = data.generateReport;
            writeBannerInfo(savedFilter.id, {status});
            writePrompt(prompt);

            // Refetch saved reports so Redux has the new AI-generated filter, then navigate
            // @ts-expect-error
            dispatch(SavedFilters.fetchSavedReports()).then(() => {
                appHistory.push(savedFilter.htmlUrlPath);
            });
        });
    };

    // ── Auto-submit from URL ?prompt= param (command palette AI action) ──
    React.useEffect(() => {
        if (autoSubmittedRef.current) return;

        const params = new URLSearchParams(window.location.search);
        const urlPrompt = params.get('prompt');

        if (urlPrompt && urlPrompt.trim()) {
            autoSubmittedRef.current = true;
            setPrompt(urlPrompt.trim());
            // Clear the param from the URL so refresh doesn't re-trigger
            appHistory.replace(window.location.pathname);
        }
    }, []);

    // Once prompt is set from URL, auto-submit on next render
    React.useEffect(() => {
        if (autoSubmittedRef.current && prompt) {
            // Reset the flag so we don't loop
            autoSubmittedRef.current = false;
            handleSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prompt]);

    return (
        <div className={styles['prompt-box-container']}>
            <Prompt
                when={true}
                message={() => {
                    // When a user navigates away we should remove the prompt from local storage so they come back to a clean slate.
                    removePrompt();

                    return true;
                }}
            />
            <PromptBox
                placeholder='I.e."Show me all sales in the last year broken down by sales rep."'
                value={prompt}
                onChange={setPrompt}
                onSubmit={handleSubmit}
                autoFocus={true}
                getTextareaRef={(ref) => {
                    textareaRef.current = ref;
                }}
                disabled={!isReportGeneratorEnabled || isLoadingReportGeneratorEnabled}
                tooltipText={
                    !isLoadingReportGeneratorEnabled && isReportGeneratorEnabled === false
                        ? 'Disabled by admin'
                        : undefined
                }
            />
            <Stack spacing={12}>
                <Body size='small' color='ai-purple'>
                    Recommendations
                </Body>
                {STARTER_PROMPTS.map(({label, prompt: promptText}) => (
                    <AiSuggestionPill
                        key={label}
                        label={label}
                        onClick={() => {
                            setPrompt(promptText);
                            focusPromptBox();
                        }}
                    />
                ))}
            </Stack>
        </div>
    );
}
